import React, { useState, useEffect, useMemo } from 'react';
import { ModelPicker, PromptLink } from './modelInfo';
import { backdropClose } from '../backdrop';

// Hints panel: one model call writes a hint for every selected riddle (points
// with a task and no hint yet, or the "no hint" placeholder). Unlike the other
// batches the modal stays open: a spinner while the writer runs, then a table
// (riddle · hint · answer) where rows are approved one by one. A row whose hint
// wants a picture carries the author's description and a "Find picture" button
// — a per-row call on the Claude subscription (a ready picture is found on the
// web, a rebus or collage is assembled from found parts), repeatable any number
// of times; the text never goes away. "Save approved" writes the ticked hints (and
// the current picture, into the hint field's gallery) into the quest. The
// table lives in the run state, so it survives a reload.

const DEFAULT_HINT = 'Для данного задания подсказка не предусмотрена';
const COLOR = '#e67e00';

const hasTask = (l) => !!(l.task || '').trim();
const hintMissing = (l) => {
  const h = String(l.hint || '').trim();
  return !h || h === DEFAULT_HINT;
};

async function papi(path, body) {
  const res = await fetch(`/api${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  let data = null;
  try { data = await res.json(); } catch (e) { /* empty body */ }
  if (!res.ok) throw new Error((data && data.error) || `HTTP ${res.status}`);
  return data;
}

const fileUrl = (questId, id, thumb) => `/api/pipeline/quest/${encodeURIComponent(questId)}/hints-file/${encodeURIComponent(id)}${thumb ? '?thumb=1' : ''}`;

const fmtElapsed = (fromIso) => {
  if (!fromIso) return '';
  const s = Math.max(0, Math.round((Date.now() - new Date(fromIso).getTime()) / 1000));
  const m = Math.floor(s / 60);
  return m ? `${m}m ${String(s % 60).padStart(2, '0')}s` : `${s}s`;
};
const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const Spinner = ({ size = 18 }) => (
  <span style={{
    display: 'inline-block', width: size, height: size, borderRadius: '50%',
    border: `${Math.max(2, Math.round(size / 7))}px solid rgba(230,126,0,0.25)`, borderTopColor: COLOR,
    animation: 'hintsSpin 0.9s linear infinite', verticalAlign: 'middle',
  }} />
);

// Full diagnostics of a failure, folded — never a one-line stub.
const ErrorFold = ({ title, errors, raw }) => {
  if ((!errors || !errors.length) && !raw) return null;
  return (
    <details style={{ fontSize: 12, color: '#c5221f', marginTop: 4 }}>
      <summary style={{ cursor: 'pointer' }}>{title || (errors && errors[0]) || 'Error'}</summary>
      <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 11, maxHeight: 240, overflow: 'auto', background: 'rgba(197,34,31,0.06)', padding: 6, borderRadius: 4 }}>
        {(errors || []).join('\n')}{raw ? `\n\n--- raw answer (tail) ---\n${raw}` : ''}
      </pre>
    </details>
  );
};

// Riddle text, clamped to a few lines until clicked.
const Clamp = ({ text, lines = 4 }) => {
  const [open, setOpen] = useState(false);
  if (!text) return <span style={{ opacity: 0.5 }}>—</span>;
  return (
    <div
      onClick={() => setOpen((o) => !o)}
      title={open ? 'Collapse' : 'Expand'}
      style={open ? { cursor: 'pointer', whiteSpace: 'pre-wrap' } : {
        cursor: 'pointer', display: '-webkit-box', WebkitLineClamp: lines, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap',
      }}
    >{text}</div>
  );
};

function PictureBox({ questId, row, onFind, onSelect, busy }) {
  const [note, setNote] = useState('');
  const pictures = row.pictures || [];
  const cur = row.picture_idx != null ? pictures[row.picture_idx] : null;
  const running = row.picture_status === 'running';
  return (
    <div style={{ marginTop: 6, padding: '6px 8px', borderRadius: 6, background: 'rgba(230,126,0,0.07)', border: '1px dashed rgba(230,126,0,0.5)' }}>
      <div style={{ fontSize: 12, fontStyle: 'italic', color: '#7a4a00' }}>🖼 {row.picture_description || '(no description)'}</div>
      {cur && (
        <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'flex-start' }}>
          <img
            src={fileUrl(questId, cur.id, true)} alt=""
            onClick={() => window.open(fileUrl(questId, cur.id), '_blank', 'noopener')}
            title="Open full size"
            style={{ width: 150, maxHeight: 120, objectFit: 'contain', borderRadius: 6, cursor: 'zoom-in', background: '#fff', border: '1px solid var(--border,#ddd)' }}
          />
          <div style={{ fontSize: 11, color: '#5f6368', minWidth: 0 }}>
            {cur.what_i_see && <div>{cur.what_i_see}</div>}
            {cur.matches && <div style={{ marginTop: 2 }}>{cur.matches}</div>}
            {cur.source_page_url && (
              <div style={{ marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <a href={cur.source_page_url} target="_blank" rel="noopener noreferrer">{cur.author_or_site || cur.source_page_url}</a>
              </div>
            )}
            {cur.kind === 'assembled' && (cur.parts || []).length > 0 && (
              <div style={{ marginTop: 2 }}>assembled from {cur.parts.length} part(s): {cur.parts.map((p, i) => (
                <span key={p.id + i}>{i ? ', ' : ''}{p.source_page_url ? <a href={p.source_page_url} target="_blank" rel="noopener noreferrer">{p.author_or_site || p.id}</a> : (p.author_or_site || p.id)}</span>
              ))}</div>
            )}
            <div style={{ marginTop: 2, opacity: 0.7 }}>{cur.kind === 'assembled' ? 'assembled' : 'found'} · try #{cur.attempt || row.picture_idx + 1} · {fmtTime(cur.at)}</div>
            {cur.feedback && <div style={{ marginTop: 2, color: '#7a4a00' }} title="The editor's note this attempt answered">↩ after note: {cur.feedback}</div>}
          </div>
        </div>
      )}
      {pictures.length > 1 && (
        <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }} title="Earlier finds — click to make one current">
          {pictures.map((p, i) => (
            <img
              key={p.id + i} src={fileUrl(questId, p.id, true)} alt=""
              title={`try #${p.attempt || i + 1}${p.feedback ? ` · after note: ${p.feedback}` : ''}`}
              onClick={() => onSelect(i)}
              style={{ width: 44, height: 34, objectFit: 'cover', borderRadius: 3, cursor: 'pointer', opacity: i === row.picture_idx ? 1 : 0.55, border: i === row.picture_idx ? `2px solid ${COLOR}` : '2px solid transparent' }}
            />
          ))}
        </div>
      )}
      {/* A note to the model about what it made: it continues the same session
          with the note (or a fresh call with the previous SVG when the session is gone). */}
      {!running && (
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={pictures.length ? 'Note to the model about this picture (what to change)…' : 'Optional guidance for the first attempt…'}
          rows={2}
          style={{ width: '100%', boxSizing: 'border-box', marginTop: 6, fontSize: 12, padding: '4px 6px', borderRadius: 4, border: '1px solid var(--border,#ccc)', resize: 'vertical' }}
        />
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
        {running
          ? <><Spinner size={14} /><span style={{ fontSize: 11, color: '#5f6368' }}>making… {fmtElapsed(row.picture_started_at)}{row.picture_note ? ` · with note: ${row.picture_note}` : ''}</span></>
          : (
            <>
              {note.trim() ? (
                <button onClick={() => { onFind(note.trim()); setNote(''); }} disabled={busy} style={{ fontSize: 12, padding: '3px 10px', borderRadius: 4, cursor: 'pointer', background: COLOR, color: '#fff', border: 'none', fontWeight: 600 }}>
                  ↩ {pictures.length ? 'Redo with note' : 'Make picture with note'}
                </button>
              ) : (
                <button onClick={() => onFind(null)} disabled={busy} style={{ fontSize: 12, padding: '3px 10px', borderRadius: 4, cursor: 'pointer' }}>
                  {pictures.length ? '↻ Make again' : '🖼 Make picture'}
                </button>
              )}
            </>
          )}
      </div>
      {(row.picture_status === 'error' || row.picture_status === 'interrupted' || row.picture_status === 'stopped') && (
        <ErrorFold title={`Picture search ${row.picture_status}: ${(row.picture_errors || [])[0] || ''}`} errors={row.picture_errors} />
      )}
      {row.picture_notes && row.picture_status !== 'error' && (
        <details style={{ fontSize: 11, color: '#5f6368', marginTop: 4 }}>
          <summary style={{ cursor: 'pointer' }}>notes</summary>
          <div style={{ whiteSpace: 'pre-wrap' }}>{row.picture_notes}</div>
        </details>
      )}
    </div>
  );
}

const HintsBatchPanel = ({ questId, runId, locations, pointsByUuid, initialBatch, embedded, onStatus }) => {
  const [batch, setBatch] = useState(initialBatch || null);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('scope'); // 'scope' | 'result'
  const [selected, setSelected] = useState({});
  const [approved, setApproved] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saveResult, setSaveResult] = useState(null);
  const [, setTick] = useState(0);
  const [cleared, setCleared] = useState(false);

  useEffect(() => {
    if (initialBatch && !batch && !cleared) setBatch(initialBatch);
  }, [initialBatch, batch, cleared]);

  const points = (locations || []).filter((l) => l.uuid && pointsByUuid && pointsByUuid[l.uuid]);
  const missingUuids = points.filter((l) => hasTask(l) && hintMissing(l)).map((l) => l.uuid);
  const allTaskUuids = points.filter(hasTask).map((l) => l.uuid);
  const selectedUuids = points.filter((l) => selected[l.uuid]).map((l) => l.uuid);
  const sameSet = (arr) => arr.length === selectedUuids.length && arr.every((u) => selected[u]);
  const activePreset = sameSet(missingUuids) ? 'missing' : sameSet(allTaskUuids) ? 'all' : 'custom';
  const setPreset = (uuids) => setSelected(Object.fromEntries(uuids.map((u) => [u, true])));

  const running = !!(batch && batch.status === 'running');
  const rows = useMemo(() => (batch && batch.status !== 'running' ? (batch.rows || []) : []), [batch]);
  const anyPictureRunning = rows.some((r) => r.picture_status === 'running');
  const savedCount = rows.filter((r) => r.saved).length;
  const approvedUuids = rows.filter((r) => approved[r.uuid] && String(r.hint || '').trim()).map((r) => r.uuid);

  // Live updates: the writer finishing, picture searches, saves — one SSE
  // channel per run, subscribed while anything can change or the modal shows.
  useEffect(() => {
    if (!runId || !(open || running || anyPictureRunning)) return undefined;
    const es = new EventSource(`/api/pipeline/runs/${runId}/events`);
    es.onmessage = (e) => {
      let msg;
      try { msg = JSON.parse(e.data); } catch (err) { return; }
      if (msg.type === 'hints_batch') {
        setBatch(msg.batch);
        if (msg.batch && msg.batch.status !== 'running') setView('result');
      }
    };
    es.onerror = () => { /* auto-reconnects */ };
    return () => es.close();
  }, [runId, open, running, anyPictureRunning]);

  // Elapsed counters tick once a second while something runs.
  useEffect(() => {
    if (!(running || anyPictureRunning)) return undefined;
    const t = setInterval(() => setTick((k) => k + 1), 1000);
    return () => clearInterval(t);
  }, [running, anyPictureRunning]);

  useEffect(() => {
    if (onStatus) onStatus({ running: running || anyPictureRunning, done: savedCount, total: rows.length, status: batch ? batch.status : null, hasBatch: !!batch });
  }, [running, anyPictureRunning, savedCount, rows.length, batch, onStatus]);

  const openScope = () => { setError(null); setPreset(missingUuids); setView('scope'); setOpen(true); };
  const openTable = () => { setError(null); setSaveResult(null); setView(running ? 'running' : 'result'); setOpen(true); };

  const start = async () => {
    if (!selectedUuids.length) { setError('Select at least one point'); return; }
    setError(null); setBusy(true);
    try {
      await papi(`/pipeline/quest/${questId}/hints-batch`, { uuids: selectedUuids });
      setCleared(false);
      setApproved({});
      setSaveResult(null);
      setBatch({ status: 'running', started_at: new Date().toISOString(), total: selectedUuids.length, rows: [] });
      setView('running');
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const stop = async () => {
    setError(null);
    try { await papi(`/pipeline/quest/${questId}/hints-batch/stop`, {}); } catch (e) { setError(e.message); }
  };

  const clear = async () => {
    if (!window.confirm('Delete this hints table? Hints already saved into the quest are kept.')) return;
    setError(null); setBusy(true);
    try {
      await papi(`/pipeline/quest/${questId}/hints-batch/clear`, {});
      setCleared(true);
      setBatch(null);
      setApproved({});
      setOpen(false);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const findPicture = async (uuid, note) => {
    setError(null);
    try { await papi(`/pipeline/quest/${questId}/hints/picture`, { uuid, note: note || null }); } catch (e) { setError(e.message); }
  };
  const selectPicture = async (uuid, idx) => {
    setError(null);
    try {
      const r = await papi(`/pipeline/quest/${questId}/hints/picture-select`, { uuid, idx });
      if (r && r.batch) setBatch(r.batch);
    } catch (e) { setError(e.message); }
  };

  const saveApproved = async () => {
    if (!approvedUuids.length) { setError('Tick at least one row with a hint'); return; }
    setError(null); setBusy(true); setSaveResult(null);
    try {
      const r = await papi(`/pipeline/quest/${questId}/hints/save`, { uuids: approvedUuids });
      setSaveResult(r);
      setApproved((a) => {
        const next = { ...a };
        (r.results || []).forEach((x) => { if (x.hint) delete next[x.uuid]; });
        return next;
      });
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const primaryBtn = { background: COLOR, color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 4, cursor: 'pointer', fontWeight: 600 };
  const stopBtn = { background: '#c5221f', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 4, cursor: 'pointer' };
  const box = embedded ? { padding: '4px 2px 2px' } : { border: '1px solid var(--border,#ddd)', borderRadius: 8, padding: '10px 12px', margin: '8px 8px 0' };
  const th = { textAlign: 'left', fontSize: 11, textTransform: 'uppercase', color: '#5f6368', padding: '6px 8px', borderBottom: '1px solid var(--border,#ddd)', position: 'sticky', top: 0, background: 'var(--bg,#fff)', zIndex: 1 };
  const td = { padding: '8px', borderBottom: '1px solid var(--border,#eee)', verticalAlign: 'top', fontSize: 13 };

  const pictureRows = rows.filter((r) => r.needs_picture).length;

  return (
    <div style={box}>
      <style>{'@keyframes hintsSpin{to{transform:rotate(360deg)}}'}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {!embedded && <span style={{ fontWeight: 700, color: COLOR }}>💡 Hints</span>}
        {!running && (
          <button style={primaryBtn} disabled={busy || !points.length} onClick={openScope}>
            Write hints…{missingUuids.length ? ` (${missingUuids.length} missing)` : ''}
          </button>
        )}
        {running && <button style={stopBtn} onClick={stop}>■ Stop</button>}
        {running && <span style={{ fontSize: 12, color: '#5f6368' }}><Spinner size={12} /> writing {batch.total} hint(s) · {fmtElapsed(batch.started_at)}</span>}
        {batch && !running && (
          <button onClick={openTable} disabled={busy} style={{ padding: '6px 12px', borderRadius: 4, cursor: 'pointer' }}>
            Open table ({rows.length} rows{savedCount ? `, ${savedCount} saved` : ''})
          </button>
        )}
        {batch && !running && batch.status !== 'done' && (
          <span style={{ fontSize: 12, color: '#c5221f' }}>{batch.status}</span>
        )}
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          <ModelPicker k="hints" /> <PromptLink k="hints" />
          {batch && !running && (
            <button className="remove-location-button" onClick={clear} disabled={busy} title="Delete this hints table — hints already saved into the quest are kept" aria-label="Delete hints table">×</button>
          )}
        </span>
      </div>
      {error && !open && <div style={{ color: '#c5221f', fontSize: 12, marginTop: 6 }}>{error}</div>}

      {open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} {...backdropClose(() => setOpen(false))}>
          <div
            style={{ background: 'var(--bg,#fff)', borderRadius: 10, padding: 20, width: view === 'scope' ? 560 : 1180, maxWidth: '96vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              💡 Hints <ModelPicker k="hints" /> <PromptLink k="hints" />
              <button onClick={() => setOpen(false)} style={{ marginLeft: 'auto', border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer' }} title="Close (a running generation continues)">×</button>
            </h3>
            {error && <div style={{ color: '#c5221f', fontSize: 12, marginBottom: 6 }}>{error}</div>}

            {view === 'scope' && (
              <>
                <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>Quick select:</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                  {[
                    { key: 'missing', label: `No hint yet (${missingUuids.length})`, uuids: missingUuids },
                    { key: 'all', label: `All with a task (${allTaskUuids.length})`, uuids: allTaskUuids },
                  ].map((p) => (
                    <button
                      key={p.key} onClick={() => setPreset(p.uuids)}
                      style={{ padding: '4px 10px', borderRadius: 14, cursor: 'pointer', fontSize: 12, border: activePreset === p.key ? `2px solid ${COLOR}` : '1px solid var(--border,#ccc)', background: activePreset === p.key ? 'rgba(230,126,0,0.10)' : 'transparent', fontWeight: activePreset === p.key ? 600 : 400 }}
                    >{p.label}</button>
                  ))}
                  <button onClick={() => setSelected({})} style={{ padding: '4px 10px', borderRadius: 14, cursor: 'pointer', fontSize: 12, border: '1px solid var(--border,#ccc)', background: 'transparent', marginLeft: 'auto' }}>Clear</button>
                </div>
                <div style={{ maxHeight: 300, overflow: 'auto', border: '1px solid var(--border,#eee)', borderRadius: 6, padding: 6 }}>
                  {points.map((l, i) => {
                    const ok = hasTask(l);
                    const missing = ok && hintMissing(l);
                    return (
                      <label key={l.uuid} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '3px 2px', cursor: ok ? 'pointer' : 'default', opacity: ok ? 1 : 0.5 }}>
                        <input type="checkbox" disabled={!ok} checked={!!selected[l.uuid]} onChange={(e) => setSelected((s) => ({ ...s, [l.uuid]: e.target.checked }))} />
                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i + 1}. {l.name}</span>
                        {!ok ? <span style={{ fontSize: 10, opacity: 0.6 }}>no task</span>
                          : missing ? <span style={{ fontSize: 10, color: COLOR }}>no hint</span>
                            : <span style={{ fontSize: 10, color: '#188038' }} title={l.hint}>has hint</span>}
                      </label>
                    );
                  })}
                </div>
                <div style={{ fontSize: 12, marginTop: 8 }}><strong>{selectedUuids.length}</strong> riddle(s) go to the writer in one call</div>
                <div style={{ fontSize: 12, color: '#7a4a00', background: 'rgba(230,126,0,0.08)', borderRadius: 6, padding: '6px 8px', marginTop: 6 }}>
                  One call writes a hint for every selected riddle (the Fes quest is the example in the prompt). The table opens here when it is done; rows are approved one by one and nothing is written into the quest until «Save approved». Picture hints get a description and a per-row «Make picture» on the Claude subscription: a ready picture is found on the web, a rebus or collage is assembled from parts found on the web.
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
                  {batch && !running && <button onClick={() => setView('result')}>Back to table</button>}
                  <button onClick={() => setOpen(false)}>Cancel</button>
                  <button style={primaryBtn} disabled={busy || selectedUuids.length === 0} onClick={start}>Write ({selectedUuids.length})</button>
                </div>
              </>
            )}

            {view === 'running' && running && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: '30px 0' }}>
                <Spinner size={44} />
                <div style={{ fontSize: 14 }}>Writing {batch.total} hint(s) with <b>{batch.model || '…'}</b></div>
                <div style={{ fontSize: 12, color: '#5f6368' }}>{fmtElapsed(batch.started_at)} · one call, usually a minute or two</div>
                <button style={stopBtn} onClick={stop}>■ Stop</button>
              </div>
            )}
            {view === 'running' && !running && batch && batch.status !== 'done' && (
              <div style={{ padding: 10 }}>
                <div style={{ color: '#c5221f', fontWeight: 600 }}>Writer {batch.status}</div>
                <ErrorFold title={(batch.errors || [])[0] || batch.status} errors={batch.errors} raw={batch.raw_tail} />
                <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
                  <button onClick={() => setView('scope')}>New run…</button>
                  <button onClick={() => setOpen(false)}>Close</button>
                </div>
              </div>
            )}

            {view === 'result' && batch && !running && (
              <>
                <div style={{ fontSize: 12, color: '#5f6368', marginBottom: 6, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <span>{batch.status === 'done' ? '✓ written' : batch.status} · {rows.length} row(s){pictureRows ? ` · ${pictureRows} with a picture` : ''}{savedCount ? ` · ${savedCount} saved` : ''}</span>
                  {batch.model && <span>model {batch.model}</span>}
                  {batch.cost != null && <span>${Number(batch.cost).toFixed(3)}</span>}
                  {batch.finished_at && <span>{fmtTime(batch.finished_at)}</span>}
                  {pictureRows > 0 && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>picture search: <ModelPicker k="hintpic" /> <PromptLink k="hintpic" /></span>}
                </div>
                {batch.status !== 'done' && <ErrorFold title={`Writer ${batch.status}: ${(batch.errors || [])[0] || ''}`} errors={batch.errors} raw={batch.raw_tail} />}
                {batch.missing && batch.missing.length > 0 && (
                  <div style={{ fontSize: 12, color: '#b06000', marginBottom: 6 }}>No hint came back for: {batch.missing.join('; ')}</div>
                )}
                {saveResult && (
                  <div style={{ fontSize: 12, color: saveResult.ok ? '#188038' : '#c5221f', marginBottom: 6 }}>
                    Saved {saveResult.saved} hint(s){saveResult.images ? `, ${saveResult.images} picture(s)` : ''}.
                    {(saveResult.results || []).filter((x) => (x.errors && x.errors.length) || (x.warnings && x.warnings.length)).map((x) => (
                      <div key={x.uuid} style={{ color: x.errors && x.errors.length ? '#c5221f' : '#b06000' }}>· {x.name}: {[...(x.errors || []), ...(x.warnings || [])].join('; ')}</div>
                    ))}
                  </div>
                )}
                <div style={{ overflow: 'auto', flex: 1, minHeight: 0, border: '1px solid var(--border,#eee)', borderRadius: 6 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ ...th, width: 28 }}>
                          <input
                            type="checkbox" title="Tick every row with a hint"
                            checked={rows.length > 0 && rows.every((r) => !String(r.hint || '').trim() || approved[r.uuid])}
                            onChange={(e) => setApproved(e.target.checked ? Object.fromEntries(rows.filter((r) => String(r.hint || '').trim()).map((r) => [r.uuid, true])) : {})}
                          />
                        </th>
                        <th style={{ ...th, width: 150 }}>Point</th>
                        <th style={{ ...th, width: '30%' }}>Riddle</th>
                        <th style={th}>Hint</th>
                        <th style={{ ...th, width: '18%' }}>Answer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => {
                        const hasHint = !!String(r.hint || '').trim();
                        return (
                          <tr key={r.uuid} style={{ background: approved[r.uuid] ? 'rgba(230,126,0,0.06)' : 'transparent' }}>
                            <td style={td}>
                              <input type="checkbox" disabled={!hasHint} checked={!!approved[r.uuid]} onChange={(e) => setApproved((a) => ({ ...a, [r.uuid]: e.target.checked }))} />
                            </td>
                            <td style={td}>
                              <div style={{ fontWeight: 600 }}>{r.n}. {r.name}</div>
                              {r.saved && (
                                <div style={{ fontSize: 11, color: '#188038', marginTop: 2 }} title={`Saved by ${r.saved.by}`}>
                                  ✓ saved {fmtTime(r.saved.at)}{r.saved.picture_id ? ' · 🖼' : ''}
                                  {r.saved.hint !== r.hint ? ' (older text)' : ''}
                                </div>
                              )}
                            </td>
                            <td style={td}><Clamp text={r.task} /></td>
                            <td style={td}>
                              {hasHint ? <div style={{ whiteSpace: 'pre-wrap' }}>{r.hint}</div> : <span style={{ color: '#c5221f' }}>(no hint returned)</span>}
                              {r.stem_hits > 0 && <span style={{ fontSize: 10, color: '#c5221f', marginLeft: 6 }} title="The text contains a stem from the forbidden list">⚠ stems: {r.stem_hits}</span>}
                              {r.needs_picture && (
                                <PictureBox questId={questId} row={r} busy={busy} onFind={(note) => findPicture(r.uuid, note)} onSelect={(i) => selectPicture(r.uuid, i)} />
                              )}
                            </td>
                            <td style={{ ...td, fontSize: 12, color: '#5f6368' }}><Clamp text={r.answer} lines={3} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
                  <button onClick={() => setView('scope')} disabled={busy}>New run…</button>
                  <span style={{ marginLeft: 'auto', fontSize: 12, color: '#5f6368' }}>{approvedUuids.length} approved</span>
                  <button onClick={() => setOpen(false)}>Close</button>
                  <button style={primaryBtn} disabled={busy || approvedUuids.length === 0} onClick={saveApproved}>Save approved ({approvedUuids.length})</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HintsBatchPanel;
